/* @flow */
import { css } from 'linaria';
import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import {
  RequestInAppBlock,
  ReviewBlock,
  ThreeFeaturesBlock,
  PreparingForYourMoveBlock,
} from '../components';
import {
  BodyText,
  Container,
  Illustration,
  SectionTitle,
  Spacer,
  Title,
} from '../../components/core';
import Colors from '../../constants/Colors';
import { PageBody } from '../../components/chrome';
import { ResponsiveEstimateForm } from '../../components/EstimateForm';
import Reviews, { getReviewTagged } from '../../../data/Reviews';
import type { ServicesPageChromeMetadata } from './ServicesPageChrome';
import tripRequests from '../../../redux/trip-requests';
import type { UseCase } from '../../../data/TripRequests';

function ProductBlock({
  title,
  description,
  product,
  figure,
}: {
  title: string,
  description: any,
  product: string,
  figure: any,
}) {
  return (
    <div className={Styles.ProductBlock}>
      {figure}
      <div className={Styles.ProductBadge}>{product}</div>
      <Title size="32" weight="bold" align="center">
        {title}
      </Title>
      <BodyText size="16" align="center" className={Styles.ProductDescription}>
        {description}
      </BodyText>
    </div>
  );
}

class SmallMovesPage extends React.Component<{
  setNewTripRequestUseCase: (useCase: UseCase) => void,
}> {
  static metadata = {
    title: 'Small Moves',
    description:
      'Get same day movers in as little as 30 minutes for all of your local small moves. Learn how to get the best movers with affordable rates and download the app. Try it today!',
  };

  static servicesMetadata: ServicesPageChromeMetadata = {
    illustration: 'luggers-moving-home',
    id: 'small-moves',
  };

  componentDidMount() {
    const { setNewTripRequestUseCase } = this.props;
    setNewTripRequestUseCase('moving');
  }

  render() {
    return (
      <PageBody>
        <Container padded={true}>
          <SectionTitle title="Move into your apartment with a tap" />
          <BodyText
            size="16"
            align="center"
            style={{ maxWidth: '630px', marginLeft: 'auto', marginRight: 'auto' }}>
            Enjoy the ease of moving with Lugg. In just few simple steps book 2 strong movers and a
            truck to move all your items into your new place.
          </BodyText>
          <Spacer size={80} />
          <ThreeFeaturesBlock
            features={[
              {
                title: 'Schedule within minutes',
                description:
                  'Don’t spend hours looking for movers. It takes a few minutes to request 2 of our best Luggers to move you.',
                icon: 'pick-a-time',
              },
              {
                title: 'Don’t lift a finger',
                description:
                  'Our Luggers will do the impossible to get all your items to your destination safely. You sit back while we do the lifting.',
                icon: 'stay-at-home',
              },
              {
                title: 'Peace of mind',
                description:
                  'We are licensed and insured. Your move with Lugg will be backed by our multi-million dollar insurance policy.',
                icon: 'shield',
              },
            ]}
          />
        </Container>
        <Spacer size={120} />
        <Container size="large" padded={true}>
          <ResponsiveEstimateForm />
        </Container>
        <Spacer size={120} />
        <Container padded={true} align="center">
          <SectionTitle title="We move it all" subtitle="From your sofa to your whole place" />
          <Spacer size={100} />
          <ProductBlock
            title="Your sofa"
            description={[
              'Whether you’re moving your sofa or just some boxes, ',
              <strong key="strong">Lugg Pickup</strong>,
              ' is the perfect vehicle for moving a few items.',
            ]}
            product="Lugg Pickup"
            figure={<Illustration name="moving-with-pickup" />}
          />
          <Spacer size={200} />
          <ProductBlock
            title="Your living room"
            description={[
              <strong key="strong">Lugg Van</strong>,
              ' is the right match for a room full of stuff, such as a living room, dining room, or bedroom.',
            ]}
            product="Lugg Van"
            figure={<Illustration name="moving-with-van" />}
          />
          <Spacer size={200} />
          <ProductBlock
            title="Your whole place"
            description={[
              <strong key="strong">Lugg XL</strong>,
              ' is great fit for moving your whole apartment, small office, and other oversized items.',
            ]}
            product="Lugg XL"
            figure={<Illustration name="moving-with-xl" />}
          />
        </Container>
        <Container size="large" padded={true}>
          <Spacer size={200} color={Colors.Divider} />
        </Container>
        <Container padded={true}>
          <RequestInAppBlock />
        </Container>
        <Spacer size={120} />
        <PreparingForYourMoveBlock />
        <Spacer size={120} />
        <Container padded={true}>
          <ReviewBlock review={getReviewTagged(Reviews, 'small-moves-page')} />
        </Container>
      </PageBody>
    );
  }
}

const Styles = {
  ProductBlock: css``,
  ProductBadge: css`
    background-color: ${Colors.Violet};
    display: inline-block;
    border-radius: 4px;
    height: 32px;
    padding: 4px 12px;
    font-size: 17px;
    font-weight: 700;
    color: ${Colors.White};
    margin: 40px 0 24px;
  `,
  ProductDescription: css`
    max-width: 500px;
    margin: 12px auto 0;
  `,
  InlineFeature: css`
    margin-bottom: 56px;
  `,
};

export default compose(
  connect(
    null,
    dispatch => ({
      setNewTripRequestUseCase: useCase =>
        dispatch(tripRequests.actions.setNewTripRequestUseCase(useCase)),
    })
  )
)(SmallMovesPage);



// WEBPACK FOOTER //
// ./src/ui/pages/ServicesPage/SmallMovesPage.js